import { z } from "zod";

export const PlanningOpaqueIdSchema = z.uuid();
export const PlanningRequestIdSchema = z.string().regex(/^req_[0-9a-f-]{36}$/u);
export const LocalDateSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/u)
  .refine((value) => {
    const [yearText, monthText, dayText] = value.split("-");
    const year = Number(yearText);
    const month = Number(monthText);
    const day = Number(dayText);
    const date = new Date(Date.UTC(year, month - 1, day));
    return (
      date.getUTCFullYear() === year &&
      date.getUTCMonth() === month - 1 &&
      date.getUTCDate() === day
    );
  }, "Invalid local calendar date");
export const LocalTimeSchema = z.string().regex(/^(?:[01]\d|2[0-3]):[0-5]\d$/u);

function hasValidTaskTimeWindow(value: {
  available_from_local_time: string | null;
  available_until_local_time: string | null;
}): boolean {
  return (
    (value.available_from_local_time === null && value.available_until_local_time === null) ||
    (value.available_from_local_time !== null &&
      value.available_until_local_time !== null &&
      value.available_until_local_time > value.available_from_local_time)
  );
}
export const TaskStateSchema = z.enum([
  "planned",
  "ready",
  "in_progress",
  "blocked",
  "done",
  "verified",
  "needs_changes",
  "deferred",
  "skipped",
  "cancelled",
]);
export const TaskCommandSchema = z.enum([
  "start",
  "complete",
  "reopen",
  "request_help",
  "clear_help",
  "verify",
  "return",
  "defer",
  "skip",
  "cancel",
  "restore",
]);
export const VerificationPolicySchema = z.enum([
  "none",
  "guardian_optional",
  "guardian_required",
  "evidence_required",
]);

export const ProjectStatusSchema = z.enum([
  "planned",
  "active",
  "paused",
  "completed",
  "cancelled",
]);

export const ProjectParticipantSchema = z
  .object({
    child_id: PlanningOpaqueIdSchema,
    display_name: z.string().min(1).max(80),
    theme_color: z.string().regex(/^#[0-9a-f]{6}$/u),
  })
  .strict();

export const MilestoneSchema = z
  .object({
    id: PlanningOpaqueIdSchema,
    workspace_id: PlanningOpaqueIdSchema,
    project_id: PlanningOpaqueIdSchema,
    title: z.string().min(1).max(240),
    description: z.string().max(4000),
    due_local_date: LocalDateSchema.nullable(),
    status: z.enum(["planned", "in_progress", "completed", "cancelled"]),
    completed_at: z.number().int().nullable(),
    version: z.number().int().min(1),
    created_at: z.number().int(),
    updated_at: z.number().int(),
  })
  .strict();

export const ProjectSchema = z
  .object({
    id: PlanningOpaqueIdSchema,
    workspace_id: PlanningOpaqueIdSchema,
    name: z.string().min(1).max(160),
    child_copy: z.string().min(1).max(160),
    description: z.string().max(4000),
    area_id: PlanningOpaqueIdSchema.nullable(),
    term_id: PlanningOpaqueIdSchema.nullable(),
    status: ProjectStatusSchema,
    start_local_date: LocalDateSchema.nullable(),
    target_local_date: LocalDateSchema.nullable(),
    color: z.string().regex(/^#[0-9a-f]{6}$/u),
    participants: z.array(ProjectParticipantSchema).min(1),
    milestones: z.array(MilestoneSchema).default([]),
    version: z.number().int().min(1),
    created_at: z.number().int(),
    updated_at: z.number().int(),
    archived_at: z.number().int().nullable(),
  })
  .strict();

export const CreateProjectRequestSchema = z
  .object({
    name: z.string().trim().min(1).max(160),
    child_copy: z.string().trim().min(1).max(160),
    description: z.string().trim().max(4000).default(""),
    area_id: PlanningOpaqueIdSchema.nullable().default(null),
    term_id: PlanningOpaqueIdSchema.nullable().default(null),
    status: z.enum(["planned", "active"]).default("active"),
    start_local_date: LocalDateSchema.nullable().default(null),
    target_local_date: LocalDateSchema.nullable().default(null),
    color: z
      .string()
      .regex(/^#[0-9a-fA-F]{6}$/u)
      .default("#4f8f72"),
    child_ids: z.array(PlanningOpaqueIdSchema).min(1).max(20),
  })
  .strict()
  .refine((value) => new Set(value.child_ids).size === value.child_ids.length, {
    message: "child_ids must be unique",
    path: ["child_ids"],
  })
  .refine(
    (value) =>
      value.start_local_date === null ||
      value.target_local_date === null ||
      value.target_local_date >= value.start_local_date,
    {
      message: "target_local_date must not be earlier than start_local_date",
      path: ["target_local_date"],
    },
  );

export const ProjectListResponseSchema = z
  .object({
    items: z.array(ProjectSchema),
    request_id: PlanningRequestIdSchema,
  })
  .strict();

export const CreateProjectResponseSchema = z
  .object({
    project: ProjectSchema,
    idempotency_replayed: z.boolean(),
    sync_cursor: z.string().min(1),
    request_id: PlanningRequestIdSchema,
  })
  .strict();

export const UpdateProjectRequestSchema = z
  .object({
    expected_version: z.number().int().min(1),
    name: z.string().trim().min(1).max(160),
    child_copy: z.string().trim().min(1).max(160),
    description: z.string().trim().max(4000),
    area_id: PlanningOpaqueIdSchema.nullable(),
    term_id: PlanningOpaqueIdSchema.nullable(),
    status: ProjectStatusSchema,
    start_local_date: LocalDateSchema.nullable(),
    target_local_date: LocalDateSchema.nullable(),
    color: z.string().regex(/^#[0-9a-fA-F]{6}$/u),
    child_ids: z.array(PlanningOpaqueIdSchema).min(1).max(20),
  })
  .strict()
  .refine((value) => new Set(value.child_ids).size === value.child_ids.length, {
    message: "child_ids must be unique",
    path: ["child_ids"],
  })
  .refine(
    (value) =>
      value.start_local_date === null ||
      value.target_local_date === null ||
      value.target_local_date >= value.start_local_date,
    {
      message: "target_local_date must not be earlier than start_local_date",
      path: ["target_local_date"],
    },
  );

export const UpdateProjectResponseSchema = z
  .object({
    project: ProjectSchema,
    idempotency_replayed: z.boolean(),
    sync_cursor: z.string().min(1),
    request_id: PlanningRequestIdSchema,
  })
  .strict();

export const ProjectArchiveCommandRequestSchema = z
  .object({ expected_version: z.number().int().min(1) })
  .strict();

export const ProjectArchiveCommandResponseSchema = z
  .object({
    project: ProjectSchema,
    command: z.enum(["archive", "restore"]),
    idempotency_replayed: z.boolean(),
    sync_cursor: z.string().min(1),
    request_id: PlanningRequestIdSchema,
  })
  .strict();

export const CreateMilestoneRequestSchema = z
  .object({
    title: z.string().trim().min(1).max(240),
    description: z.string().trim().max(4000).default(""),
    due_local_date: LocalDateSchema.nullable().default(null),
  })
  .strict();

export const CreateMilestoneResponseSchema = z
  .object({
    milestone: MilestoneSchema,
    idempotency_replayed: z.boolean(),
    sync_cursor: z.string().min(1),
    request_id: PlanningRequestIdSchema,
  })
  .strict();

export const MilestoneCommandRequestSchema = z
  .object({ expected_version: z.number().int().min(1) })
  .strict();

export const MilestoneCommandResponseSchema = z
  .object({
    milestone: MilestoneSchema,
    command: z.enum(["start", "complete"]),
    idempotency_replayed: z.boolean(),
    sync_cursor: z.string().min(1),
    request_id: PlanningRequestIdSchema,
  })
  .strict();

export const CreateAssignedTaskRequestSchema = z
  .object({
    child_id: PlanningOpaqueIdSchema,
    project_id: PlanningOpaqueIdSchema.nullable().default(null),
    title: z.string().trim().min(1).max(240),
    child_copy: z.string().trim().min(1).max(160),
    planned_local_date: LocalDateSchema.nullable(),
    due_local_date: LocalDateSchema.nullable().default(null),
    available_from_local_time: LocalTimeSchema.nullable().default(null),
    available_until_local_time: LocalTimeSchema.nullable().default(null),
    today_pinned: z.boolean().default(false),
    estimated_minutes: z.number().int().min(0).max(1440).nullable().default(null),
    priority: z.number().int().min(0).max(4).default(0),
    verification_policy: VerificationPolicySchema.default("none"),
    evidence_policy: z
      .object({ required: z.enum(["none", "photo", "note", "photo_or_note"]).default("none") })
      .strict()
      .default({ required: "none" }),
  })
  .strict()
  .refine(
    (value) =>
      value.planned_local_date === null ||
      value.due_local_date === null ||
      value.due_local_date >= value.planned_local_date,
    {
      message: "due_local_date must not be earlier than planned_local_date",
      path: ["due_local_date"],
    },
  )
  .refine(hasValidTaskTimeWindow, {
    message: "available time window must contain an increasing from/until pair",
    path: ["available_until_local_time"],
  })
  .refine(
    (value) =>
      (value.verification_policy === "evidence_required") ===
      (value.evidence_policy.required !== "none"),
    {
      message: "evidence_required verification must name a required evidence type",
      path: ["evidence_policy"],
    },
  );

export const TaskAssignmentSchema = z
  .object({
    id: PlanningOpaqueIdSchema,
    workspace_id: PlanningOpaqueIdSchema,
    task_id: PlanningOpaqueIdSchema,
    child_id: PlanningOpaqueIdSchema,
    state: TaskStateSchema,
    sort_key: z.string().min(1).max(200),
    last_actor_source: z.enum(["self", "adult_proxy", "supporter_proxy", "agent", "system"]),
    started_at: z.number().int().nullable(),
    blocked_at: z.number().int().nullable(),
    done_at: z.number().int().nullable(),
    verified_at: z.number().int().nullable(),
    version: z.number().int().min(1),
    updated_at: z.number().int(),
  })
  .strict();

export const TaskSchema = z
  .object({
    id: PlanningOpaqueIdSchema,
    workspace_id: PlanningOpaqueIdSchema,
    project_id: PlanningOpaqueIdSchema.nullable().default(null),
    title: z.string().min(1).max(240),
    child_copy: z.string().min(1).max(160),
    planned_local_date: LocalDateSchema.nullable(),
    due_local_date: LocalDateSchema.nullable(),
    timezone: z.string().min(1).max(100).nullable(),
    available_from_local_time: LocalTimeSchema.nullable(),
    available_until_local_time: LocalTimeSchema.nullable(),
    today_pinned: z.boolean(),
    estimated_minutes: z.number().int().min(0).max(1440).nullable(),
    priority: z.number().int().min(0).max(4),
    status: TaskStateSchema,
    verification_policy: VerificationPolicySchema,
    evidence_policy: z
      .object({ required: z.enum(["none", "photo", "note", "photo_or_note"]) })
      .strict()
      .default({ required: "none" }),
    version: z.number().int().min(1),
    updated_at: z.number().int(),
  })
  .strict();

export const TodayTaskSchema = z
  .object({
    task: TaskSchema,
    project: z
      .object({
        id: PlanningOpaqueIdSchema,
        name: z.string().min(1).max(160),
        child_copy: z.string().min(1).max(160),
        color: z.string().regex(/^#[0-9a-f]{6}$/u),
      })
      .strict()
      .nullable()
      .default(null),
    assignment: TaskAssignmentSchema,
    help: z
      .object({
        id: PlanningOpaqueIdSchema,
        reason: z.enum(["stuck", "need_explanation", "need_material", "need_time", "other"]),
        note: z.string().max(1000),
        requested_at: z.number().int(),
      })
      .strict()
      .nullable(),
  })
  .strict();

export const CreateAssignedTaskResponseSchema = z
  .object({
    item: TodayTaskSchema,
    idempotency_replayed: z.boolean(),
    sync_cursor: z.string().min(1),
    request_id: PlanningRequestIdSchema,
  })
  .strict();

export const TodayTaskListResponseSchema = z
  .object({
    child_id: PlanningOpaqueIdSchema,
    local_date: LocalDateSchema,
    items: z.array(TodayTaskSchema),
    request_id: PlanningRequestIdSchema,
  })
  .strict();

export const CarryoverTaskSchema = TodayTaskSchema.extend({
  missed_planned_local_date: LocalDateSchema,
}).strict();

export const CarryoverTaskListResponseSchema = z
  .object({
    child_id: PlanningOpaqueIdSchema,
    local_date: LocalDateSchema,
    items: z.array(CarryoverTaskSchema),
    next_cursor: z.string().min(1).nullable(),
    request_id: PlanningRequestIdSchema,
  })
  .strict();

export const TodayTaskOrderItemSchema = z
  .object({
    task_id: PlanningOpaqueIdSchema,
    assignment_id: PlanningOpaqueIdSchema,
    expected_version: z.number().int().min(1),
  })
  .strict();

export const TodayTaskReorderRequestSchema = z
  .object({
    child_id: PlanningOpaqueIdSchema,
    local_date: LocalDateSchema,
    items: z.array(TodayTaskOrderItemSchema).min(2).max(50),
  })
  .strict()
  .superRefine((value, context) => {
    if (new Set(value.items.map((item) => item.task_id)).size !== value.items.length) {
      context.addIssue({
        code: "custom",
        message: "task_id values must be unique",
        path: ["items"],
      });
    }
    if (new Set(value.items.map((item) => item.assignment_id)).size !== value.items.length) {
      context.addIssue({
        code: "custom",
        message: "assignment_id values must be unique",
        path: ["items"],
      });
    }
  });

export const TodayTaskReorderResponseSchema = z
  .object({
    child_id: PlanningOpaqueIdSchema,
    local_date: LocalDateSchema,
    items: z.array(TodayTaskSchema).min(2).max(50),
    idempotency_replayed: z.boolean(),
    sync_cursor: z.string().min(1),
    request_id: PlanningRequestIdSchema,
  })
  .strict();

export const ScheduledTaskSchema = TodayTaskSchema.extend({
  schedule_local_date: LocalDateSchema,
}).strict();

export const TaskListResponseSchema = z
  .object({
    child_id: PlanningOpaqueIdSchema,
    date_from: LocalDateSchema,
    date_to: LocalDateSchema,
    items: z.array(ScheduledTaskSchema),
    next_cursor: z.string().min(1).nullable(),
    request_id: PlanningRequestIdSchema,
  })
  .strict();

export const BacklogTaskListResponseSchema = z
  .object({
    child_id: PlanningOpaqueIdSchema,
    items: z.array(TodayTaskSchema),
    next_cursor: z.string().min(1).nullable(),
    request_id: PlanningRequestIdSchema,
  })
  .strict();

export const TaskCommandRequestSchema = z
  .object({
    assignment_id: PlanningOpaqueIdSchema,
    expected_version: z.number().int().min(1),
    reason: z.enum(["stuck", "need_explanation", "need_material", "need_time", "other"]).optional(),
    note: z.string().trim().max(1000).optional(),
  })
  .strict();

export const TaskCommandResponseSchema = z
  .object({
    item: TodayTaskSchema,
    command: TaskCommandSchema,
    idempotency_replayed: z.boolean(),
    sync_cursor: z.string().min(1),
    request_id: PlanningRequestIdSchema,
  })
  .strict();

export const RescheduleTaskRequestSchema = z
  .object({
    assignment_id: PlanningOpaqueIdSchema,
    expected_version: z.number().int().min(1),
    planned_local_date: LocalDateSchema.nullable(),
    due_local_date: LocalDateSchema.nullable(),
    available_from_local_time: LocalTimeSchema.nullable().optional(),
    available_until_local_time: LocalTimeSchema.nullable().optional(),
    today_pinned: z.boolean().optional(),
  })
  .strict()
  .refine(
    (value) =>
      value.planned_local_date === null ||
      value.due_local_date === null ||
      value.due_local_date >= value.planned_local_date,
    {
      message: "due_local_date must not be earlier than planned_local_date",
      path: ["due_local_date"],
    },
  )
  .superRefine((value, context) => {
    const hasFrom = value.available_from_local_time !== undefined;
    const hasUntil = value.available_until_local_time !== undefined;
    if (hasFrom !== hasUntil) {
      context.addIssue({
        code: "custom",
        message: "available time window updates require both from and until",
        path: [hasFrom ? "available_until_local_time" : "available_from_local_time"],
      });
      return;
    }
    if (
      hasFrom &&
      hasUntil &&
      !hasValidTaskTimeWindow({
        available_from_local_time: value.available_from_local_time ?? null,
        available_until_local_time: value.available_until_local_time ?? null,
      })
    ) {
      context.addIssue({
        code: "custom",
        message: "available time window must contain an increasing from/until pair",
        path: ["available_until_local_time"],
      });
    }
  });

export const RescheduleTaskResponseSchema = z
  .object({
    item: TodayTaskSchema,
    idempotency_replayed: z.boolean(),
    sync_cursor: z.string().min(1),
    request_id: PlanningRequestIdSchema,
  })
  .strict();

export const UpdateTaskRequestSchema = z
  .object({
    assignment_id: PlanningOpaqueIdSchema,
    expected_version: z.number().int().min(1),
    title: z.string().trim().min(1).max(240),
    child_copy: z.string().trim().min(1).max(160),
    estimated_minutes: z.number().int().min(0).max(1440).nullable(),
    due_local_date: LocalDateSchema.nullable().optional(),
  })
  .strict();

export const UpdateTaskResponseSchema = z
  .object({
    item: TodayTaskSchema,
    idempotency_replayed: z.boolean(),
    sync_cursor: z.string().min(1),
    request_id: PlanningRequestIdSchema,
  })
  .strict();

export type CreateAssignedTaskRequest = z.infer<typeof CreateAssignedTaskRequestSchema>;
export type CreateAssignedTaskResponse = z.infer<typeof CreateAssignedTaskResponseSchema>;
export type TodayTask = z.infer<typeof TodayTaskSchema>;
export type TodayTaskListResponse = z.infer<typeof TodayTaskListResponseSchema>;
export type CarryoverTask = z.infer<typeof CarryoverTaskSchema>;
export type CarryoverTaskListResponse = z.infer<typeof CarryoverTaskListResponseSchema>;
export type TodayTaskOrderItem = z.infer<typeof TodayTaskOrderItemSchema>;
export type TodayTaskReorderRequest = z.infer<typeof TodayTaskReorderRequestSchema>;
export type TodayTaskReorderResponse = z.infer<typeof TodayTaskReorderResponseSchema>;
export type ScheduledTask = z.infer<typeof ScheduledTaskSchema>;
export type TaskListResponse = z.infer<typeof TaskListResponseSchema>;
export type BacklogTaskListResponse = z.infer<typeof BacklogTaskListResponseSchema>;
export type TaskCommandRequest = z.infer<typeof TaskCommandRequestSchema>;
export type TaskCommandResponse = z.infer<typeof TaskCommandResponseSchema>;
export type RescheduleTaskRequest = z.infer<typeof RescheduleTaskRequestSchema>;
export type RescheduleTaskResponse = z.infer<typeof RescheduleTaskResponseSchema>;
export type UpdateTaskRequest = z.infer<typeof UpdateTaskRequestSchema>;
export type UpdateTaskResponse = z.infer<typeof UpdateTaskResponseSchema>;
export type Project = z.infer<typeof ProjectSchema>;
export type CreateProjectRequest = z.infer<typeof CreateProjectRequestSchema>;
export type CreateProjectResponse = z.infer<typeof CreateProjectResponseSchema>;
export type ProjectListResponse = z.infer<typeof ProjectListResponseSchema>;
export type UpdateProjectRequest = z.infer<typeof UpdateProjectRequestSchema>;
export type UpdateProjectResponse = z.infer<typeof UpdateProjectResponseSchema>;
export type ProjectArchiveCommandRequest = z.infer<typeof ProjectArchiveCommandRequestSchema>;
export type ProjectArchiveCommandResponse = z.infer<typeof ProjectArchiveCommandResponseSchema>;
export type Milestone = z.infer<typeof MilestoneSchema>;
export type CreateMilestoneRequest = z.infer<typeof CreateMilestoneRequestSchema>;
export type CreateMilestoneResponse = z.infer<typeof CreateMilestoneResponseSchema>;
export type MilestoneCommandRequest = z.infer<typeof MilestoneCommandRequestSchema>;
export type MilestoneCommandResponse = z.infer<typeof MilestoneCommandResponseSchema>;
